import { useState } from 'react';
import {
  Title,
  Text,
  Progress,
  Group,
  Box,
  Collapse,
  UnstyledButton,
} from '@mantine/core';
import { Container, ContentWrapper, LogsWrapper } from './styles';
import { t } from 'i18next';
import { usePluginView } from './use-plugin-view';
import { FiChevronDown } from 'react-icons/fi';
import { usePluginRows } from '@morph-mapper/plugins';
import {
  PluginLogList,
  PluginProgressRows,
  getBarColor,
  isRowReady,
  overallProgress,
  type PluginLog,
  type PluginRow,
} from '../../../common/features/plugin-progress';

export interface PluginViewProps {
  children: (progress: () => void, disabled?: boolean) => JSX.Element;
}

// ─── Summary — the whole run in one line ─────────────────────────────────────

/** "Show details" or "Hide details", with a chevron that turns when open. */
const DetailsToggle = ({ open }: { open: boolean }) => (
  <>
    <Text fz="xs" c="dimmed">
      {open ? t('plugins.hideDetails') : t('plugins.showDetails')}
    </Text>
    <FiChevronDown
      size={14}
      style={{
        transform: open ? 'rotate(180deg)' : undefined,
        transition: 'transform 150ms ease',
      }}
    />
  </>
);

const DETAILS_ID = 'plugin-progress-details';

const PluginSummary = ({
  rows,
  open,
  onToggle,
}: {
  rows: PluginRow[];
  open: boolean;
  onToggle: () => void;
}) => {
  const ready = rows.filter(isRowReady).length;
  const allReady = ready === rows.length;
  const overall = overallProgress(rows);

  return (
    <UnstyledButton
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={DETAILS_ID}
      w="100%"
      sx={{ borderRadius: 6 }}
    >
      <Group position="apart" mb={overall === undefined ? 0 : 6}>
        <Text fz="sm" fw={500}>
          {t('plugins.summary', { ready, total: rows.length })}
        </Text>
        <Group spacing={6}>
          {overall !== undefined && (
            <Text fz="xs" fw={700} c={allReady ? 'green' : 'blue'}>
              {overall}%
            </Text>
          )}
          <DetailsToggle open={open} />
        </Group>
      </Group>
      {overall !== undefined && (
        <Progress
          value={overall}
          size="lg"
          radius="md"
          color={allReady ? 'green' : getBarColor(overall)}
          striped={!allReady}
          animate={!allReady}
        />
      )}
    </UnstyledButton>
  );
};

// ─── Container — always rendered to avoid layout shift ───────────────────────

/**
 * Collapsed to one summary line by default: with a row for every active
 * plugin, the list pushed the logs and the Confirm button off screen (#1132).
 * The rows stay mounted while collapsed, so their timers and animations keep
 * running and are current when opened.
 */
const PluginProgress = () => {
  const rows = usePluginRows();
  const [open, setOpen] = useState(false);

  // Always render the container — avoids layout jump when entries first arrive
  return (
    <Box mb="lg" sx={{ minHeight: 4 }}>
      {rows.length > 0 && (
        <>
          <PluginSummary rows={rows} open={open} onToggle={() => setOpen((o) => !o)} />
          <Collapse in={open} id={DETAILS_ID}>
            <Box mt="md">
              <PluginProgressRows rows={rows} />
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
};

// ─── Logs, collapsed the same way as the progress rows ──────────────────────

const LOGS_ID = 'plugin-logs-details';

const PluginLogs = ({ logs }: { logs: PluginLog[] }) => {
  const [open, setOpen] = useState(false);

  return (
    <LogsWrapper>
      <UnstyledButton
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={LOGS_ID}
        w="100%"
        sx={{ borderRadius: 6 }}
      >
        <Group position="apart">
          <Text fw={500}>
            {t('title.logs')} ({logs.length})
          </Text>
          <Group spacing={6}>
            <DetailsToggle open={open} />
          </Group>
        </Group>
      </UnstyledButton>
      <Collapse in={open} id={LOGS_ID}>
        <Box mt="md">
          <PluginLogList logs={logs} />
        </Box>
      </Collapse>
    </LogsWrapper>
  );
};

// ─── View ────────────────────────────────────────────────────────────────────

export const PluginView = ({ children }: PluginViewProps) => {
  const { progressSetup, disableNext, displayLogs } = usePluginView();
  return (
    <>
      <Container>
        <ContentWrapper>
          <Title order={3} fw={500}>
            {t('title.pluginView')}
          </Title>
          <PluginProgress />
          <PluginLogs logs={displayLogs} />
        </ContentWrapper>
      </Container>

      {children(progressSetup, disableNext)}
    </>
  );
};
